import { put, takeEvery } from 'redux-saga/effects';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { QcsSyncResourcesConfigProps } from '../core/resourceStoreTypes';
import * as alertsActions from './alertsActions';
import * as alertsSelectors from './alertsSelectors';
import { transformResponseToAlert } from './alertsTransformers';
import type { AlertProps } from './alertsTypes';

const { qcsSyncGetResources, qcsSyncUpdateResource, qcsSyncDeleteResource, qcsSyncUpdateResources } = resourceSagaUtils;
const { getLastSyncDate } = alertsSelectors;

const mkQcsSyncAlertsConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<AlertProps>();
const mkResourceConfig = (props: Partial<QcsSyncResourcesConfigProps<AlertProps>>) =>
  mkQcsSyncAlertsConfig({
    resourceName: 'alerts',
    resourceBaseUrl: '/alert/alerts',
    getLastSyncDate,
    transformResponseToResources: transformResponseToAlert,
    ...props,
  });

function* getAlertsActionWatcher() {
  const config = mkResourceConfig({
    successAction: alertsActions.getAlertsSuccess,
    failureAction: alertsActions.getAlertsFailure,
  });
  yield takeEvery(alertsActions.getAlerts.type, qcsSyncGetResources<AlertProps>, config);
}

function* updateAlertActionWatcher() {
  const config = mkResourceConfig({
    successAction: alertsActions.updateAlertSuccess,
    failureAction: alertsActions.updateAlertFailure,
  });
  yield takeEvery(alertsActions.updateAlert.type, qcsSyncUpdateResource<AlertProps>, config);
}

function* deleteAlertActionWatcher() {
  const config = mkResourceConfig({
    successAction: alertsActions.deleteAlertSuccess,
    failureAction: alertsActions.deleteAlertFailure,
  });
  yield takeEvery(alertsActions.deleteAlert.type, qcsSyncDeleteResource<AlertProps>, config);
}

function* batchAlertsActionWatcher() {
  const config = mkResourceConfig({
    successAction: alertsActions.batchAlertsSuccess,
    failureAction: alertsActions.batchAlertsFailure,
  });
  yield takeEvery(alertsActions.batchAlerts.type, qcsSyncUpdateResources<AlertProps>, config);
}

function* retryOnFailure() {
  yield takeEvery(
    [alertsActions.updateAlertFailure, alertsActions.deleteAlertFailure, alertsActions.batchAlertsFailure],
    function* handler(action) {
      if ((action.payload as Error)?.message !== 'demoMode') {
        yield put(alertsActions.getAlerts());
      }
    },
  );
}

export default [
  getAlertsActionWatcher,
  updateAlertActionWatcher,
  deleteAlertActionWatcher,
  batchAlertsActionWatcher,
  retryOnFailure,
];
